







import { useState, useEffect } from "react";
import {
  createExpenseCategoryApi,
  getExpenseCategoriesApi,
  updateExpenseCategoryApi,
  deleteExpenseCategoryApi,
} from "../../app/api";
import { createPortal } from "react-dom";
import {
  Tag,
  Plus,
  Edit3,
  Trash2,
  RotateCcw,
  CheckCircle2,
  X,
  Layers,
  ChevronRight,
  AlertCircle
} from "lucide-react";

export default function Categories() {
  const [categoryName, setCategoryName] = useState("");
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [editModal, setEditModal] = useState({
    show: false,
    id: null,
    name: "",
  });

  const [deleteModal, setDeleteModal] = useState({
    show: false,
    id: null,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setIsLoading(true);
    const res = await getExpenseCategoriesApi();
    if (res.success) setCategories(res.data || []);
    setIsLoading(false);
  };

  const handleAddCategory = async () => {
    if (!categoryName.trim()) return alert("Identify your category");
    const res = await createExpenseCategoryApi({ name: categoryName });
    if (res.success) {
      setCategoryName("");
      loadCategories();
    } else {
      alert("Registration failed");
    }
  };

  const handleUpdateCategory = async () => {
    const res = await updateExpenseCategoryApi(editModal.id, { name: editModal.name });
    if (res.success) {
      setEditModal({ show: false, id: null, name: "" });
      loadCategories();
    } else {
      alert("Update failed");
    }
  };

  const handleDeleteCategory = async () => {
    const res = await deleteExpenseCategoryApi(deleteModal.id);
    if (res.success) {
      setDeleteModal({ show: false, id: null });
      loadCategories();
    } else {
      alert("System could not purge");
    }
  };

  return (
    // <div className="max-w-4xl mx-auto md:mt-10 px-4 mb-20">
    <div className="max-w-8xl mx-auto md:mt-20 md:mb-20 ">
      <div className="
      relative overflow-hidden
      rounded-[20px] py-8 md:py-12 px-2 md:px-12
      bg-gradient-to-br from-[#1e174a] to-[#0f0c29]/95
      backdrop-blur-2xl text-white
      border border-white/10
      shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)]
    ">
        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12 text-white px-2">
          <div className="flex items-center gap-4">
            <div className="p-3.5 mt-[-34px] md:mt-0 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-xl shadow-purple-500/20">
              <Layers className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-white ">Expense Categories</h1>
              <p className="text-white/60 text-sm">Classify expenditures for organizational tracking</p>
            </div>
          </div>
        </div>

        {/* SEARCH/ADD BAR */}
        <div className=" mt-[-24px] p-6 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 blur-3xl group-hover:bg-purple-500/10 transition-all " />
          <div className="flex flex-col md:flex-row gap-4 relative z-10">
            <div className="flex md:flex-row flex-col gap-2 relative">
              <Tag className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
              <input
                placeholder="Category name"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                className=" bg-white/5 border border-white/10 rounded-lg px-10 py-2 text-sm font-bold text-white outline-none focus:border-purple-500/30 transition-all shadow-inner"
              />

              <button
                onClick={handleAddCategory}
                className="px-8 py-3 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-black text-xs  shadow-xl shadow-purple-900/40 transition-all active:scale-95 flex items-start justify-start gap-2"
              >
                ADD
              </button>
            </div>
          </div>
        </div>

        {/* CATEGORY GRID */}
        <div className="w-full overflow-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl">

          {/* TABLE HEADER */}
          <div className="grid grid-cols-12 px-4 py-3 text-[11px] font-bold uppercase tracking-widest text-white/40 border-b border-white/10">
            <div className="col-span-8">Category</div>
            <div className="col-span-4 text-left">Actions</div>
          </div>

          {/* TABLE BODY */}
          <div className="divide-y divide-white/5">
            {isLoading ? (
              Array(5).fill(0).map((_, i) => (
                <div key={i} className="h-14 animate-pulse bg-white/5" />
              ))
            ) : categories.length === 0 ? (
              <div className="py-20 flex flex-col items-center gap-4 opacity-10">
                <RotateCcw className="w-12 h-12" />
                <span className="text-[10px] font-black uppercase tracking-[3px]">
                  NO CATEGORIES DEFINED
                </span>
              </div>
            ) : (
              categories.map((c) => (
                <div
                  key={c.id}
                  className="group grid grid-cols-12 items-center px-4 py-4 hover:bg-white/[0.06] transition-all duration-300"
                >
                  {/* CATEGORY NAME */}
                  <div className="col-span-8">
                    <h3 className="text-white font-semibold text-sm">{c.name}</h3>
                  </div>

                  {/* ACTIONS (HOVER ONLY) */}
                  <div className="col-span-4 flex justify-left gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <button
                      onClick={() => setEditModal({ show: true, id: c.id, name: c.name })}
                      className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-600 hover:text-white transition"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => setDeleteModal({ show: true, id: c.id })}
                      className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-600 hover:text-white transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* EDIT MODAL PORTAL */}
        {editModal.show && createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setEditModal({ show: false })} />
            <div className="relative w-full max-w-sm bg-gradient-to-br from-[#1e174a] to-[#0f0c29] rounded-[40px] p-10 text-center border border-white/20 shadow-2xl animate-scaleIn">
              <div className="w-20 h-20 bg-blue-500/20 rounded-[28px] flex items-center justify-center mx-auto mb-6">
                <Edit3 className="w-10 h-10 text-blue-400" />
              </div>
              <h3 className="text-2xl font-black text-white mb-6">Edit Category</h3>
              <div className="space-y-6">
                <div className="relative group">
                  <Tag className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20 group-focus-within:text-blue-400" />
                  <input
                    value={editModal.name}
                    onChange={(e) => setEditModal({ ...editModal, name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-[24px] px-14 py-4 text-sm font-bold text-white outline-none focus:border-blue-500/30 transition-all shadow-inner"
                    placeholder="New label..."
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <button onClick={handleUpdateCategory} className="w-full py-4 rounded-2xl bg-blue-600 text-white font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition shadow-xl shadow-blue-900/40">
                    COMMIT CHANGE
                  </button>
                  <button onClick={() => setEditModal({ show: false })} className="w-full py-4 rounded-2xl bg-white/5 border border-white/10 text-white/40 font-black text-xs uppercase tracking-widest">
                    CANCEL
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* DELETE MODAL PORTAL */}
        {deleteModal.show && createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setDeleteModal({ show: false })} />
            <div className="relative w-full max-w-sm bg-gradient-to-br from-[#1e174a] to-[#0f0c29] rounded-[40px] p-10 text-center border border-white/20 shadow-2xl animate-scaleIn">
              <div className="w-20 h-20 bg-red-500/20 rounded-[28px] flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-2">Delete Category?</h3>
              <p className="text-sm font-medium text-white/40 mb-8 leading-relaxed">This will remove the record. Past records will lose.</p>
              <div className="flex flex-col gap-3">
                <button onClick={handleDeleteCategory} className="w-full py-4 rounded-2xl bg-red-600 text-white font-black text-xs uppercase tracking-widest hover:bg-red-700 transition shadow-xl shadow-red-900/40">
                  CONFIRM Delete
                </button>
                <button onClick={() => setDeleteModal({ show: false })} className="w-full py-4 rounded-2xl bg-white/5 border border-white/10 text-white/60 font-black text-xs uppercase tracking-widest">
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>

  );
}







// import { useState, useEffect } from "react";
// import {
//   createExpenseCategoryApi,
//   getExpenseCategoriesApi,
//   updateExpenseCategoryApi,
//   deleteExpenseCategoryApi,
// } from "../../app/api";
// import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
// import { createPortal } from "react-dom";
// export default function Categories() {
//   const [categoryName, setCategoryName] = useState("");
//   const [categories, setCategories] = useState([]);

//   const [editModal, setEditModal] = useState({
//     show: false,
//     id: null,
//     name: "",
//   });

//   const [deleteModal, setDeleteModal] = useState({
//     show: false,
//     id: null,
//   });

//   useEffect(() => {
//     loadCategories();
//   }, []);

//   const loadCategories = async () => {
//     const res = await getExpenseCategoriesApi();
//     if (res.success) setCategories(res.data || []);
//   };

//   const handleAddCategory = async () => {
//     if (!categoryName.trim()) return alert("Enter name");

//     const res = await createExpenseCategoryApi({ name: categoryName });
//     if (res.success) {
//       setCategoryName("");
//       loadCategories();
//     } else {
//       alert("Failed");
//     }
//   };

//   const handleUpdateCategory = async () => {
//     const res = await updateExpenseCategoryApi(editModal.id, {
//       name: editModal.name,
//     });
//     if (res.success) {
//       setEditModal({ show: false, id: null, name: "" });
//       loadCategories();
//     } else {
//       alert("Update failed");
//     }
//   };

//   const handleDeleteCategory = async () => {
//     const res = await deleteExpenseCategoryApi(deleteModal.id);
//     if (res.success) {
//       setDeleteModal({ show: false, id: null });
//       loadCategories();
//     } else {
//       alert("Delete failed");
//     }
//   };

//   return (
//     <div
//       className="
//       rounded-2xl
//       p-6
//       md:mt-20
//       bg-gradient-to-br
//       from-[#2d1c6a]
//       via-[#24155a]
//       to-[#12082f]
//       text-white
//       border border-white/10
//       shadow-2xl shadow-[#12082F]/60
//       relative
//     "
//     >
//       {/* glow */}
//       <div className="absolute -top-10 -right-10 w-60 h-60 bg-purple-500/20 blur-3xl rounded-full" />
//       <div className="absolute bottom-0 -left-10 w-60 h-60 bg-fuchsia-500/20 blur-3xl rounded-full" />

//       <div className="relative z-10">
//         <h3 className="font-bold  text-2xl mb-5">
//           Expense Categories
//         </h3>

//         {/* ADD */}
//         <div className="flex flex-col md:flex-row gap-3 mb-6">
//           <input
//             placeholder="Category name"
//             value={categoryName}
//             onChange={(e) => setCategoryName(e.target.value)}
//             className="
//               bg-white/10 border border-white/20
//               rounded-lg px-4 py-2 text-sm
//               focus:outline-none focus:ring-2 focus:ring-purple-500/40
//               w-full md:w-80
//             "
//           />

//           <button
//             onClick={handleAddCategory}
//             className="
//               px-5 py-2 rounded-lg
//               bg-purple-600 hover:bg-purple-500
//               shadow
//             "
//           >
//             Add
//           </button>
//         </div>

//         {/* TABLE */}
//         <div className="max-h-[360px] overflow-y-auto">
//           <table className="w-full text-sm">
//             <thead className="text-white/70 border-b border-white/10">
//               <tr>
//                 <th className="py-2 text-left">Category</th>
//                 <th className="text-left">Actions</th>
//               </tr>
//             </thead>

//             <tbody>
//               {categories.length > 0 ? (
//                 categories.map((c) => (
//                   <tr
//                     key={c.id}
//                     className="border-b border-white/5 hover:bg-white/5 transition"
//                   >
//                     <td className="py-3">{c.name}</td>
//                     <td className="flex gap-3 py-3">
//                       <button
//                         onClick={() =>
//                           setEditModal({
//                             show: true,
//                             id: c.id,
//                             name: c.name,
//                           })
//                         }
//                         className="text-blue-300 hover:text-blue-100"
//                       >
//                         <PencilSquareIcon className="w-5 h-5" />
//                       </button>

//                       <button
//                         onClick={() =>
//                           setDeleteModal({ show: true, id: c.id })
//                         }
//                         className="text-red-300 hover:text-red-100"
//                       >
//                         <TrashIcon className="w-5 h-5" />
//                       </button>
//                     </td>
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td colSpan="2" className="py-6 text-center opacity-60">
//                     No categories found
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* EDIT MODAL */}
//       {/* {editModal.show && (
//         <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20">
//           <div className="bg-[#1c1045] border border-white/20 rounded-xl p-6 w-80">
//             <h4 className="mb-3">Edit Category</h4>
//             <input
//               value={editModal.name}
//               onChange={(e) =>
//                 setEditModal({ ...editModal, name: e.target.value })
//               }
//               className="
//                 w-full bg-white/10 border border-white/20
//                 rounded-lg px-3 py-2 text-sm mb-4
//               "
//             />
//             <div className="flex justify-end gap-3">
//               <button
//                 onClick={() => setEditModal({ show: false })}
//                 className="px-4 py-1 bg-white/10 rounded"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleUpdateCategory}
//                 className="px-4 py-1 bg-purple-600 rounded"
//               >
//                 Update
//               </button>
//             </div>
//           </div>
//         </div>
//       )} */}





// {editModal.show &&
//   createPortal(
//     <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center">
//       <div
//         className="
//         w-full max-w-md
//         bg-white/10 backdrop-blur-xl
//         border border-white/20
//         rounded-2xl
//         shadow-2xl
//         text-white
//         overflow-hidden
//       "
//       >
//         {/* Header */}
//         <div className="px-6 py-4 bg-gradient-to-r from-[#2d1c6a] to-[#24155a]">
//           <h4 className="font-semibold text-lg">Edit Category</h4>
//         </div>

//         {/* Body */}
//         <div className="p-6 space-y-4">
//           <input
//             value={editModal.name}
//             onChange={(e) =>
//               setEditModal({ ...editModal, name: e.target.value })
//             }
//             className="
//               w-full
//               bg-white/10 border border-white/20
//               rounded-lg px-4 py-2
//               focus:outline-none focus:ring-2 focus:ring-purple-500/40
//             "
//             placeholder="Category name"
//           />

//           <div className="flex justify-end gap-3 pt-2">
//             <button
//               onClick={() =>
//                 setEditModal({ show: false, id: null, name: "" })
//               }
//               className="px-4 py-2 bg-white/10 rounded-lg"
//             >
//               Cancel
//             </button>

//             <button
//               onClick={handleUpdateCategory}
//               className="px-5 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg"
//             >
//               Update
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>,
//     document.body
//   )}






//       {/* DELETE MODAL */}
//       {/* {deleteModal.show && (
//         <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20">
//           <div className="bg-[#1c1045] border border-red-400/30 rounded-xl p-6 w-80 text-center">
//             <p className="mb-4">Delete this category?</p>
//             <div className="flex justify-center gap-4">
//               <button
//                 onClick={() => setDeleteModal({ show: false })}
//                 className="px-4 py-1 bg-white/10 rounded"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleDeleteCategory}
//                 className="px-4 py-1 bg-red-600 rounded"
//               >
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )} */}





// {deleteModal.show &&
//   createPortal(
//     <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center">
//       <div
//         className="
//         w-full max-w-md
//         bg-white/10 backdrop-blur-xl
//         border border-red-400/30
//         rounded-2xl
//         shadow-2xl
//         text-white
//         overflow-hidden
//       "
//       >
//         {/* Header */}
//         <div className="px-6 py-4 bg-gradient-to-r from-[#2d1c6a] to-[#24155a]">
//           <h4 className="font-semibold text-lg text-red-300">
//             Confirm Delete
//           </h4>
//         </div>

//         {/* Body */}
//         <div className="p-6 text-center space-y-5">
//           <p className="text-white/80">
//             Are you sure you want to delete this category?
//           </p>

//           <div className="flex justify-center gap-4">
//             <button
//               onClick={() =>
//                 setDeleteModal({ show: false, id: null })
//               }
//               className="px-5 py-2 bg-white/10 rounded-lg"
//             >
//               Cancel
//             </button>

//             <button
//               onClick={handleDeleteCategory}
//               className="px-5 py-2 bg-red-600 hover:bg-red-500 rounded-lg"
//             >
//               Delete
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>,
//     document.body
//   )}

//     </div>
//   );
// }
